import { ZoomInIcon, ZoomOutIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import {
  Cropper,
  CropperCropArea,
  CropperDescription,
  CropperImage,
} from "@krea/shared/design-system/Cropper";
import { AppModal } from "@krea/shared/design-system/modal/app-modal";
import { Slider } from "@krea/shared/design-system/Slider";
import type { FileWithPreview } from "@krea/shared/hooks/useFileUpload";

interface ICropModalProps {
  file: FileWithPreview;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  removeFile: (id: string) => void;
  addFile: (file: File) => void;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) =>
      reject(error as unknown as Error),
    );
    image.setAttribute("crossOrigin", "anonymous"); // Needed for canvas Tainted check
    image.src = url;
  });

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number = pixelCrop.width, // Optional: specify output size
  outputHeight: number = pixelCrop.height,
): Promise<Blob | null> {
  try {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    // Set canvas size to desired output size
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Draw the cropped image onto the canvas
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth, // Draw onto the output size
      outputHeight,
    );

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg"); // Specify format and quality if needed
    });
  } catch (error) {
    console.error("Error in getCroppedImg:", error);

    return null;
  }
}

export const CropModal = ({
  file,
  isOpen,
  setIsOpen,
  removeFile,
  addFile,
}: ICropModalProps) => {
  // State to store the desired crop area in pixels
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setCroppedAreaPixels(null);
      setZoom(1);
    }
  }, [isOpen]);

  // State for zoom level

  // Callback for Cropper to provide crop data
  const handleCropChange = useCallback(
    (
      pixels: { x: number; y: number; width: number; height: number } | null,
    ) => {
      setCroppedAreaPixels(pixels);
    },
    [],
  );

  const handleApply = async () => {
    // Check if we have the necessary data
    if (!file.preview || !file.id || !croppedAreaPixels) {
      console.error("Missing data for apply:", {
        previewUrl: file.preview,
        fileId: file.id,
        croppedAreaPixels,
      });

      // Remove file if apply is clicked without crop data?
      if (file.id) {
        removeFile(file.id);
        setCroppedAreaPixels(null);
      }

      return;
    }

    try {
      // 1. Get the cropped image blob using the helper
      const croppedBlob = await getCroppedImg(file.preview, croppedAreaPixels);
      removeFile(file.id);

      if (!croppedBlob) {
        throw new Error("Failed to generate cropped image blob.");
      }

      // 2. Create a NEW object URL from the cropped blob

      const newFile = new File([croppedBlob], file.file.name, {
        type: croppedBlob.type || "image/jpeg",
      });
      addFile(newFile);

      // 5. Close the dialog (don't remove the file yet)
      setIsOpen(false);
    } catch (error) {
      console.error("Error during apply:", error);
      // Close the dialog even if cropping fails
      setIsOpen(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2 px-4">
      <Button variant="secondary" onClick={() => setIsOpen(false)}>
        Cancel
      </Button>
      <Button onClick={handleApply} disabled={!croppedAreaPixels}>
        Apply Crop
      </Button>
    </div>
  );

  return (
    <AppModal
      open={isOpen}
      setOpen={setIsOpen}
      title="Crop Image"
      onClose={() => setIsOpen(false)}
      footer={footer}
      className="max-w-2xl"
    >
      {file.preview && (
        <Cropper
          className="h-96"
          image={file.preview}
          zoom={zoom}
          onCropChange={handleCropChange}
          onZoomChange={setZoom}
        >
          <CropperDescription />
          <CropperImage />
          <CropperCropArea />
        </Cropper>
      )}
      <div className="border-t px-4 py-6">
        <div className="mx-auto flex w-full max-w-80 items-center gap-4">
          <ZoomOutIcon
            className="shrink-0 opacity-60"
            size={16}
            aria-hidden="true"
          />
          <Slider
            defaultValue={[1]}
            value={[zoom]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={(value) => setZoom(value[0] ?? 1)}
            aria-label="Zoom slider"
          />
          <ZoomInIcon
            className="shrink-0 opacity-60"
            size={16}
            aria-hidden="true"
          />
        </div>
      </div>
    </AppModal>
  );
};
